import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from 'recharts';
import { Eye, FileText, ExternalLink } from 'lucide-react';

const en = (j: any) => (typeof j === 'string' ? j : j?.en || '');

const RANGES = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

export default function NewsAnalytics() {
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string>('all');

  const { data: articles = [] } = useQuery({
    queryKey: ['news-analytics-articles'],
    queryFn: async () => {
      const { data } = await supabase
        .from('news_articles')
        .select('id, slug, title, category, view_count, published_at, is_published')
        .order('published_at', { ascending: false });
      return data || [];
    },
  });

  const { data: events = [] } = useQuery({
    queryKey: ['news-analytics-events', days],
    queryFn: async () => {
      const since = new Date(Date.now() - days * 86400000).toISOString();
      const { data } = await supabase
        .from('news_view_events')
        .select('article_id, created_at')
        .gte('created_at', since)
        .limit(50000);
      return data || [];
    },
  });

  const trend = useMemo(() => {
    const buckets = new Map<string, number>();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86400000);
      buckets.set(d.toISOString().slice(0, 10), 0);
    }
    for (const v of events) {
      if (selected !== 'all' && v.article_id !== selected) continue;
      const day = (v.created_at as string).slice(0, 10);
      if (buckets.has(day)) buckets.set(day, (buckets.get(day) || 0) + 1);
    }
    return [...buckets.entries()].map(([date, views]) => ({ date: date.slice(5), views }));
  }, [events, days, selected]);

  const perArticleInRange = useMemo(() => {
    const map = new Map<string, number>();
    for (const v of events) map.set(v.article_id, (map.get(v.article_id) || 0) + 1);
    return map;
  }, [events]);

  const filteredArticles = useMemo(() => {
    return articles
      .filter((a: any) => !search.trim() || en(a.title).toLowerCase().includes(search.toLowerCase()))
      .map((a: any) => ({ ...a, range_views: perArticleInRange.get(a.id) || 0 }))
      .sort((a: any, b: any) => b.range_views - a.range_views);
  }, [articles, search, perArticleInRange]);

  const top10 = filteredArticles.slice(0, 10).map((a: any) => ({
    title: en(a.title).slice(0, 28),
    views: a.range_views,
  }));

  const totals = {
    articles: articles.length,
    published: articles.filter((a: any) => a.is_published).length,
    rangeViews: events.length,
    allTimeViews: articles.reduce((s: number, a: any) => s + (a.view_count || 0), 0),
  };

  return (
    <div className="container max-w-7xl py-6 space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold">News analytics</h1>
          <p className="text-sm text-muted-foreground">Per-article view counts powered by news_view_events.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RANGES.map((r) => <SelectItem key={r.days} value={String(r.days)}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" asChild><Link to="/dashboard/news">Back to articles</Link></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={FileText} label="Published" value={totals.published} sub={`${totals.articles} total`} />
        <Kpi icon={Eye} label="Views in range" value={totals.rangeViews} />
        <Kpi icon={Eye} label="All-time views" value={totals.allTimeViews} />
        <Kpi icon={FileText} label="Articles tracked" value={Math.min(articles.length, perArticleInRange.size)} sub="with views in range" />
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base">Views over time</CardTitle>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="w-72"><SelectValue placeholder="All articles" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All articles</SelectItem>
              {articles.map((a: any) => <SelectItem key={a.id} value={a.id}>{en(a.title) || a.slug}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="views" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Top 10 articles in range</CardTitle></CardHeader>
        <CardContent style={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={top10} layout="vertical" margin={{ left: 40 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="title" tick={{ fontSize: 11 }} width={180} />
              <Tooltip />
              <Bar dataKey="views" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base">Per-article breakdown</CardTitle>
          <Input placeholder="Search…" className="max-w-xs" value={search} onChange={(e) => setSearch(e.target.value)} />
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="p-3">Title</th>
                <th className="p-3">Category</th>
                <th className="p-3">Range views</th>
                <th className="p-3">All-time</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredArticles.map((a: any) => (
                <tr key={a.id} className="border-t">
                  <td className="p-3">
                    <Link to={`/dashboard/news/${a.id}/edit`} className="font-medium hover:text-primary">
                      {en(a.title) || '(untitled)'}
                    </Link>
                    <div className="text-[11px] text-muted-foreground">/{a.slug}</div>
                  </td>
                  <td className="p-3">{a.category || '—'}</td>
                  <td className="p-3 font-semibold">{a.range_views}</td>
                  <td className="p-3">{a.view_count || 0}</td>
                  <td className="p-3 text-right">
                    {a.slug && <a href={`/news/${a.slug}`} target="_blank" rel="noopener noreferrer" className="text-primary inline-flex items-center gap-1 text-xs"><ExternalLink className="h-3 w-3" /> View</a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub }: any) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {label}</div>
        <div className="text-2xl font-bold mt-1">{value.toLocaleString()}</div>
        {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
}
